import { RootState } from "store";
import { AppThunk, createAppAsyncThunk } from "appThunk";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { loadClusterCostActions, StoreCostActions, DataType } from "./storeCostActions";
import { ClusterStore } from "./clusterStore";
import { DataWrapper } from "domain/dataWrapper";
import { selectCostReductionOpportunityByStore, selectCostsByStores, selectSelectedStoreByCostType, selectReferenceDate } from "modules/customer/insights/cost/costSlice";
import { setupCube } from "modules/helpers/cube/cubeSlice";
import { notifyError } from "modules/notifications/notificationsSlice";

import _ from "lodash";
import { DateTime } from "luxon";
import mathUtils from "utils/mathUtils";
import { median } from "mathjs";

import { SortDirection, numberSortExpression } from "utils/sortUtils";

export enum ClusterStoresSortField {
    StoreName,
    CostAsPercentageOfRevenue,
    CostValue,
    Variance,
    SimilarityScore
}

interface ClusterStoresSort {
    field: ClusterStoresSortField,
    direction: SortDirection
}

interface StoreCostActionsState {
    isLoading: boolean,
    hasErrors: boolean,
    storeCostActions: StoreCostActions[],
    clusterStores: ClusterStore[],
    clusterStoresSort: ClusterStoresSort
}

const initialState: StoreCostActionsState = {
    isLoading: false,
    hasErrors: false,
    storeCostActions: [],
    clusterStores: [],
    clusterStoresSort: {
        field: ClusterStoresSortField.StoreName,
        direction: SortDirection.ASC
    }
};

interface LoadStoreCostActionsResponse {
    storeCostActions: StoreCostActions[];
}

const storeCostActionsSlice = createSlice({
    name: 'customer/insights/cost/storeCostActions',
    initialState,
    reducers: {
        clearStoreCostActions: (state) => {
            state.storeCostActions = initialState.storeCostActions;
        },
        chooseAllClusterStores: (state) => {
            state.clusterStores.forEach(item => item.setIsSelected(true));
        },
        clearClusterStores: (state) => {
            state.clusterStores = initialState.clusterStores;
        },
        deselectAllClusterStores: (state) => {
            state.clusterStores.forEach(item => item.setIsSelected(false));
        },
        setClusterStores: (state, action: PayloadAction<ClusterStore[]>) => {
            state.clusterStores = action.payload;
        },
        setClusterStoresSort: (state, action: PayloadAction<ClusterStoresSort>) => {
            state.clusterStoresSort = action.payload;
        },
        toggleClusterStoreIsSelected: (state, action: PayloadAction<number | string>) => {
            const id = action.payload;
            state.clusterStores.find(item => item.id === id)?.toggleIsSelected();
        },
    },
    extraReducers: (builder: any) => {
        builder
            .addCase(loadCostActions.pending, (state: StoreCostActionsState) => {
                state.isLoading = true;
                state.hasErrors = false;
                state.storeCostActions = initialState.storeCostActions;
            })
            .addCase(loadCostActions.rejected, (state: StoreCostActionsState) => {
                state.isLoading = false;
                state.hasErrors = true;
                state.storeCostActions = initialState.storeCostActions;
            })
            .addCase(loadCostActions.fulfilled, (state: StoreCostActionsState, action: PayloadAction<LoadStoreCostActionsResponse>) => {
                state.isLoading = false;
                state.hasErrors = false;
                state.storeCostActions = action.payload.storeCostActions;
            });
    }
});

export const {
    chooseAllClusterStores,
    deselectAllClusterStores,
    setClusterStores,
    setClusterStoresSort,
    toggleClusterStoreIsSelected
} = storeCostActionsSlice.actions;

export const loadCostActions = createAppAsyncThunk(
    "customer/insights/cost/storeCostActions/loadStoreCostActions",
    async (arg, thunkAPI) => {
        try {
            await thunkAPI.dispatch(setupCube());
            const state = thunkAPI.getState();

            const store = selectSelectedStoreByCostType(state);

            if (!store) {
                return [];
            }

            const referenceDate = selectReferenceDate(state);
            const costsByStore = selectCostsByStores(state);
            const costReductionOpportunityByStore = selectCostReductionOpportunityByStore(state);
            const costsByStores = selectCostsByStores(state);
            const selectedStoreCostReductionOpportunity = selectSelectedStoreByCostType(state);
            const similarStoresIds = costsByStore.data
                .find(item => item.storeId === store?.storeId
                    && item.costName === store.costName
                )?.similarStores
                .map(similarStore => similarStore.id);

            const selectedStoreCosts = costsByStores.data.find(item =>
                item.storeId === selectedStoreCostReductionOpportunity?.storeId
                && item.costName === selectedStoreCostReductionOpportunity.costName
            );

            if (!selectedStoreCostReductionOpportunity || !selectedStoreCosts) {
                return [];
            }

            const clusterStoreIDs = selectedStoreCosts.similarStores.map(item => item.id);

            const clusterStores = costReductionOpportunityByStore.data
                .filter(item => clusterStoreIDs.includes(item.storeId ?? "")
                    && (item.costName === selectedStoreCostReductionOpportunity?.costName))
                .map(clusterStore => {
                    const similarityScore =
                        selectedStoreCosts.similarStores.find(similarStore => similarStore.id === clusterStore.storeId)?.similarityScore ?? 0;
                    return new ClusterStore(
                        clusterStore.storeId,
                        true,
                        clusterStore.storeName ?? "",
                        clusterStore.costAsPercentageOfRevenue,
                        clusterStore.costValue,
                        selectedStoreCostReductionOpportunity?.costValue - clusterStore.costValue,
                        similarityScore
                    );
                })
                .sort((a, b) => numberSortExpression(a.similarityScore, b.similarityScore, SortDirection.DESC));

            thunkAPI.dispatch(setClusterStores(clusterStores));

            const storeIds: number[] = [store?.storeId];
            if (similarStoresIds) {
                storeIds.push(...similarStoresIds);
            }

            const storeCostActionsPromise = thunkAPI.dispatch(
                loadClusterCostActions(storeIds, store?.costName, referenceDate)
            );

            const results = await Promise.all([
                storeCostActionsPromise
            ]);

            const LoadStoreCostActionsResponse: LoadStoreCostActionsResponse = {
                storeCostActions: results[0]
            };

            return LoadStoreCostActionsResponse;
        }
        catch (error) {
            thunkAPI.dispatch(notifyError("Error loading StoreCostActions."));
            return thunkAPI.rejectWithValue(null);
        }
    }
);

export const ClearStoreCostActions = (): AppThunk => (dispatch) => {
    dispatch(storeCostActionsSlice.actions.clearStoreCostActions());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.insights.cost.storeCostActions.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.insights.cost.storeCostActions.hasErrors;
};

export const selectClusterStores = (state: RootState) => {
    return state.customer.insights.cost.storeCostActions.clusterStores;
};

export const selectStoreCostActions = (state: RootState) => {
    return state.customer.insights.cost.storeCostActions.storeCostActions;
};

export const selectFilteredCostActions = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectStoreCostActions,
    selectClusterStores,
    (state: RootState) => selectSelectedStoreByCostType(state),
    (isLoading, hasErrors, storeCostActions, clusterStores, store) => {
        const costActions: DataWrapper<any[]> = {
            isLoading: isLoading,
            hasErrors: hasErrors,
            data: []
        };

        const clusterStoresIds = clusterStores
            .filter(store => store.isSelected())
            .map(store => store.id);

        const clusterData = storeCostActions.filter(costAction => clusterStoresIds.includes(costAction.storeId));

        const actionClusterAverage: { [action: string]: number | DateTime | null } = {};

        const groupedActionStoreValues = _(clusterData).groupBy(item => item.action).value();
        for (const actionKey in groupedActionStoreValues) {
            const actionStoreValues: StoreCostActions[] = [];
            const storeValues = groupedActionStoreValues[actionKey];
            if (storeValues) {
                const storeValueGroups = _(storeValues).groupBy(item => item.storeId).value();
                for (const storeKey in storeValueGroups) {
                    const store = storeValueGroups[storeKey];
                    if (store) {
                        store.sort((a, b) => b.dateId - a.dateId);
                        actionStoreValues.push(store[0]);
                    }
                }
            }

            if (actionStoreValues.length === 0) {
                actionClusterAverage[actionKey] = null;
                continue;
            }

            if (actionStoreValues.length === 1) {
                actionClusterAverage[actionKey] = actionStoreValues[0].storeValue;
            }

            actionStoreValues.sort((a, b) => {
                if ((a.dataType === DataType.Date && b.dataType === DataType.Date) ||
                    (a.dataType === DataType.Time && b.dataType === DataType.Time)) {
                    return (a.storeValue as DateTime).millisecond - (b.storeValue as DateTime).millisecond;
                }
                else {
                    return (b.storeValue as number) - (b.storeValue as number);
                }
            });

            const midpoint = (actionStoreValues.length - 1) / 2;
            const lowerRecord = actionStoreValues[Math.floor(midpoint)];
            const upperRecord = actionStoreValues[Math.ceil(midpoint)];
            const lowerValue = lowerRecord?.storeValue;
            const upperValue = upperRecord?.storeValue;

            if (!lowerRecord ||
                !upperRecord ||
                !lowerValue ||
                !upperValue ||
                (lowerRecord.dataType !== upperRecord.dataType)) {
                continue;
            }

            switch (lowerRecord.dataType) {
                case DataType.Date: {
                    if (lowerValue === upperValue) {
                        actionClusterAverage[actionKey] = lowerValue;
                        break;
                    }
                    const lowerDate = lowerValue as DateTime;
                    const upperDate = upperValue as DateTime;
                    actionClusterAverage[actionKey] = lowerDate.plus(upperDate.diff(lowerDate).milliseconds / 2);
                    break;
                }
                case DataType.Time: {
                    if (lowerValue === upperValue) {
                        actionClusterAverage[actionKey] = lowerValue;
                        break;
                    }
                    const lowerDate = lowerValue as DateTime;
                    const upperDate = upperValue as DateTime;
                    actionClusterAverage[actionKey] = lowerDate.plus(upperDate.diff(lowerDate).toObject().milliseconds ?? 0 / 2);
                    break;
                }
                case DataType.Integer: {
                    const lowerNum = lowerValue as number;
                    const upperNumber = upperValue as number;
                    actionClusterAverage[actionKey] = mathUtils.round(median([lowerNum, upperNumber]), 0);
                    break;
                }
                case DataType.Decimal: {
                    const lowerNum = lowerValue as number;
                    const upperNumber = upperValue as number;
                    actionClusterAverage[actionKey] = mathUtils.round(median([lowerNum, upperNumber]), 4);
                    break;
                }
            }
        }


        costActions.data = storeCostActions
            .filter(costAction => costAction.storeId === store?.storeId)
            .map(costAction => new StoreCostActions(
                costAction.id,
                costAction.storeId,
                costAction.action,
                costAction.storeValue,
                costAction.targetValue,
                costAction.targetVariance,
                actionClusterAverage[costAction.action],
                costAction.dataType,
                costAction.dateId
            ));

        return costActions;
    }
);



export default storeCostActionsSlice;
